/**
 * IMBEE-8069 (T86) — the welcome banner (Area A) and bottom quick-button bar (Area C)
 * portions of the widget settings returned by GET /widget.
 *
 * These mirror the service contract exactly (web-chat-widget.interface.ts). Every field is
 * optional: a channel configured before this feature shipped, or one with the feature off,
 * simply omits them, and the widget must render exactly as it does today.
 */

export enum WelcomeBannerImageMode {
    Default = 'default',
    Client = 'client',
}

export enum BottomButtonConditionType {
    TicketTag = 'ticketTag',
}

export type WelcomeBannerBadge = {
    /** 6-digit hex, no leading '#'. */
    color: string;
    text: string;
};

export type WelcomeBannerButton = {
    label: string;
    badge?: WelcomeBannerBadge;
};

export type BottomButtonDisplayCondition = {
    type: BottomButtonConditionType;
    tag: string;
};

export type BottomButton = {
    label: string;
    displayCondition?: BottomButtonDisplayCondition;
};

/**
 * The Area A + Area C slice of the widget settings, travelling as one prop from App down to
 * Conversation rather than eight separate ones through three components.
 */
export type QuickReplyConfig = {
    enableWelcomeBanner?: boolean;
    welcomeBannerImageMode?: WelcomeBannerImageMode;
    welcomeBannerImageUrl?: string | null;
    welcomeBannerButtons?: WelcomeBannerButton[];
    enableBottomButtons?: boolean;
    bottomButtons?: BottomButton[];
    /**
     * Tags on the visitor's current ticket, for AC-C.1 display conditions.
     *
     * NOT YET SUPPLIED: GET /widget returns channel configuration, not per-visitor state, and
     * nothing in the widget knows the ticket's tags today. Until a source exists this stays
     * empty, so tag-conditional buttons never show — deliberately the safe direction, since a
     * conditional button exists precisely so that some visitors do not see it.
     */
    activeTags?: string[];
};

/** AC-A.2 — the banner shows this many buttons at a time, then pages. */
export const WELCOME_BANNER_BUTTONS_PER_PAGE = 4;

/**
 * AC-C.1 — a bottom button with no displayCondition is always shown. One WITH a condition is
 * shown only when it matches, and an incomplete condition hides the button rather than
 * failing open: showing a conditional button to everyone is the worse failure, because the
 * whole point of the condition is that some visitors should not see it.
 */
export const isBottomButtonVisible = (button: BottomButton, activeTags: string[] = []): boolean => {
    const condition = button.displayCondition;
    if (!condition) return true;
    if (condition.type !== BottomButtonConditionType.TicketTag) return false;
    if (!condition.tag) return false;
    return activeTags.includes(condition.tag);
};

/** The bottom buttons this visitor should see, in configured order. */
export const visibleBottomButtons = (buttons: BottomButton[] = [], activeTags: string[] = []): BottomButton[] =>
    buttons.filter((button) => isBottomButtonVisible(button, activeTags));

/**
 * AC-A.2 — the banner's buttons split into pages. Order is preserved; the last page may be
 * short. An empty list yields no pages at all, so the banner can hide its pager entirely
 * rather than render "page 1 of 0".
 */
export const paginateBannerButtons = (
    buttons: WelcomeBannerButton[] = [],
    perPage: number = WELCOME_BANNER_BUTTONS_PER_PAGE,
): WelcomeBannerButton[][] => {
    const size = Math.max(1, perPage);
    const pages: WelcomeBannerButton[][] = [];
    for (let i = 0; i < buttons.length; i += size) {
        pages.push(buttons.slice(i, i + size));
    }
    return pages;
};

/**
 * AC-A.3 — a badge renders only when it carries BOTH a colour and a text. Half a badge is an
 * empty coloured pill, which reads as a rendering bug rather than as "no badge".
 */
export const hasRenderableBadge = (button: WelcomeBannerButton): boolean =>
    Boolean(button.badge && button.badge.color && button.badge.text);
